/**
 * Lingue (§21).
 *
 * L'insieme delle lingue è **estensibile e configurabile**: la specifica chiede
 * "almeno IT/EN", non esattamente IT/EN. Nessun punto del codice deve assumere
 * che le lingue siano due, né che l'italiano sia sempre presente.
 */
/** BCP-47 ridotto: `it`, `en`, `de`, oppure `it-CH`, `en-GB`. */
export const LANGUAGE_CODE_PATTERN = /^[a-z]{2}(-[A-Z]{2})?$/;
export function isLanguageCode(value) {
    return LANGUAGE_CODE_PATTERN.test(value);
}
export class InvalidLanguageConfigError extends Error {
}
/**
 * Costruisce la configurazione lingue da una stringa `it,en`.
 * Fallisce esplicitamente su input malformato: una lingua sbagliata in
 * configurazione significherebbe mostrare al visitatore un'informativa nella
 * lingua sbagliata, che è un problema privacy, non un dettaglio cosmetico.
 */
export function parseLanguageConfig(raw) {
    const codes = raw
        .split(',')
        .map((c) => c.trim())
        .filter((c) => c.length > 0);
    if (codes.length === 0) {
        throw new InvalidLanguageConfigError('Nessuna lingua configurata.');
    }
    for (const code of codes) {
        if (!isLanguageCode(code)) {
            throw new InvalidLanguageConfigError(`Codice lingua non valido: "${code}".`);
        }
    }
    const unique = [...new Set(codes)];
    if (unique.length !== codes.length) {
        throw new InvalidLanguageConfigError('Lingue duplicate in configurazione.');
    }
    // `codes.length > 0` è già garantito sopra.
    return { supported: unique, fallback: unique[0] };
}
/**
 * Risolve la lingua da usare per il visitatore.
 *
 * Attenzione: il fallback è una decisione privacy, non solo di UI. Restituire
 * la lingua di fallback è accettabile per l'interfaccia, ma il chiamante NON
 * deve usare questo risultato per presentare un testo di consenso in una lingua
 * che il visitatore non ha scelto — vedi `assertConsentLanguageAvailable`.
 */
export function resolveLanguage(requested, config) {
    if (requested && config.supported.includes(requested))
        return requested;
    // Degrada `it-CH` a `it` se la variante regionale non è configurata.
    const base = requested?.split('-')[0];
    if (base && config.supported.includes(base))
        return base;
    return config.fallback;
}
export class ConsentLanguageUnavailableError extends Error {
    requested;
    constructor(requested) {
        super(`Nessun testo di consenso disponibile in "${requested}". ` +
            'Raccogliere il consenso in una lingua diversa da quella scelta dal ' +
            'visitatore lo renderebbe non informato (§21, EDPB 05/2020).');
        this.requested = requested;
    }
}
/**
 * Il consenso si raccoglie solo se il testo esiste nella lingua effettivamente
 * mostrata al visitatore. Non esiste fallback silenzioso per i testi di consenso:
 * meglio non raccogliere il consenso che raccoglierlo non informato.
 */
export function assertConsentLanguageAvailable(requested, availableLanguages) {
    if (!availableLanguages.includes(requested)) {
        throw new ConsentLanguageUnavailableError(requested);
    }
}
//# sourceMappingURL=language.js.map